import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, useHistory, useParams } from "react-router-dom";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";

// Config
import { mediaCategory } from "src/config/values";
import { useAppInsights } from "src/utils/telemetry/AppInsights";
import colors from "src/config/colors";
import useTranslate from "src/hooks/useTranslate";
import { getRoute, urls } from "src/config/routes";

// State
import {
  clearMediaError,
  getMedia,
  getMediaItem,
  deleteMedia
} from "src/state/mediaPerpetrators/actions";

// Components
import GalleryHandler from "src/components/GalleryHandler";
import LoadingSection from "src/components/LoadingSection";
import MediaReuploadProvider from "src/components/MediaItemList/MediaReuploadProvider";
import Text from "src/components/Text";
import UserAddition from "src/components/UserAddition";

// Perpetrator Components
import MediaPerpetratorsItem from "./MediaPerpetratorsItem";
import AddPerpetratorWrapper from "./AddPerpetratorWrapper";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1)
  },
  title: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1)
  },
  switchText: {
    fontSize: 16,
    marginRight: 38
  },
  subTitle: {
    marginBottom: 12
  },
  perpetratorItem: {
    display: "flex",
    flexDirection: "column"
  },
  perpetratorContent: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    alignItems: "flex-start"
  },
  imageWrapper: {
    width: 254
  },
  textBox: {
    borderRadius: theme.shape.borderRadius,
    borderColor: colors.mystic,
    border: "1px solid",
    padding: "10px 14px",
    flex: 1
  },
  description: {
    color: colors.santasGray
  }
}));

const IncidentPerpetrators = ({ contents, canAddMore, showDelete }) => {
  const classes = useStyles();
  const getText = useTranslate();
  const dispatch = useDispatch();
  const history = useHistory();
  const { caseId } = useParams();
  const { trackException } = useAppInsights();

  const { currentCase, data, isLoading, isDeleting, error } = useSelector(
    state => state.mediaPerpetrators
  );

  const hasContents = contents && contents.length > 0;
  const hasMediaPerpetrators = data && data.length > 0;

  useEffect(() => {
    return () => {
      dispatch(clearMediaError());
    };
  }, [dispatch]);

  useEffect(() => {
    if ((currentCase !== caseId || !data) && !error && !isLoading) {
      dispatch(
        getMedia({
          caseId,
          category: mediaCategory.PERPETRATOR,
          onFail: () =>
            trackException(`Failed to get media from case: ${caseId}`)
        })
      );
    }
  }, [caseId, currentCase, data, dispatch, error, isLoading, trackException]);

  const handleDelete = itemId => () =>
    dispatch(
      deleteMedia({
        itemId,
        caseId,
        onFail: () =>
          trackException(
            `Failed to delete perpetrator item: ${itemId} at case: ${caseId}`
          )
      })
    );
  const handleFetchImage = mediaId => () => {
    dispatch(
      getMediaItem({
        caseId,
        mediaId,
        onFail: () =>
          trackException(
            `Failed to get media item: ${mediaId} at case: ${caseId}`
          )
      })
    );
  };

  const handleSelect = itemId => () => {
    history.push(getRoute(urls.incidentGallery, { caseId, itemId }));
  };

  const showAddPerpetrator = !hasContents && !hasMediaPerpetrators;

  return (
    <>
      {isLoading ? (
        <LoadingSection normalHeight noBackground />
      ) : (
        <div className={classes.root}>
          <Text
            className={classes.title}
            type="heading4"
            text="caseIncidentsPage.perpetratorsLabel"
          />
          {hasContents &&
            contents.map((item, index) => (
              <div className={classes.userAddition} key={item.id}>
                <UserAddition
                  text={item.text}
                  subTitle={`${getText("caseIncidentsPage.perpetratorLabel")} ${
                    index + 1
                  }`}
                  author={item.author}
                  date={item.creationDateTime}
                />
              </div>
            ))}
          <MediaReuploadProvider page="incidentPage" caseId={caseId}>
            {hasMediaPerpetrators &&
              data.map((item, index) => (
                <MediaPerpetratorsItem
                  index={hasContents ? contents.length + index : index}
                  item={item}
                  isDeleting={isDeleting}
                  key={item.id}
                  showDelete={showDelete}
                  onDelete={handleDelete(item.id)}
                  onFetchImage={handleFetchImage(item.id)}
                  onClick={handleSelect(item.id)}
                />
              ))}
          </MediaReuploadProvider>
          {canAddMore && <AddPerpetratorWrapper open={showAddPerpetrator} />}
        </div>
      )}
      <Route
        path={urls.incidentGallery}
        exact
        render={renderProps =>
          data &&
          data.length > 0 && (
            <GalleryHandler
              {...renderProps}
              data={data}
              defaultRoute={urls.incident}
              detailRoute={urls.incidentGallery}
              view="mediaPerpetrators"
            />
          )
        }
      />
    </>
  );
};

IncidentPerpetrators.propTypes = {
  canAddMore: PropTypes.bool.isRequired,
  contents: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showDelete: PropTypes.bool.isRequired
};

export default IncidentPerpetrators;
